import { preferencesStorageCache, zonedDate } from "@repo/lib";
import { useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import { Button } from "@/components/button";
import { useGraphQL, useGraphQLMutation } from "@/hooks/use-graphql";
import { UnexpectedErrorDrawer } from "@/components/unexpected-error-drawer";

const GetLatestTermsOfUseQuery = graphql(`
  query GetLatestTermsOfUseQuery {
    termsOfUse: terms_of_use(order_by: { release_date: desc }, limit: 1) {
      file
      id
      release_date
    }
  }
`);

const InsertUserTermsOfUseMutation = graphql(`
  mutation InsertTermsOfUseAgreement(
    $agreedFromIp: String!
    $termsOfUseId: Int!
    $livanceCoreId: Int!
  ) {
    insertTerms: insert_users_terms_of_use_agreed(
      objects: {
        terms_of_use_id: $termsOfUseId
        livance_core_id: $livanceCoreId
        agreed_from_ip: $agreedFromIp
        user_id: null
      }
    ) {
      affected_rows
    }
  }
`);

interface GeneralTermsAcceptButtonProps {
  memberIP: string;
}

export const GeneralTermsAcceptButton = ({
  memberIP,
}: GeneralTermsAcceptButtonProps): JSX.Element => {
  const { user } = useRouteContext({ strict: false });
  const searchParams = useSearch({
    from: "/general-terms/",
  });
  const router = useRouter();

  const { data: latestTermsOfUseVersion } = useGraphQL(GetLatestTermsOfUseQuery);
  const { mutateAsync: InsertUserTermsOfUseMutationAsync, isPending } =
    useGraphQLMutation(InsertUserTermsOfUseMutation);

  const onClickHandler = async (): Promise<void> => {
    const lastTermOfUse = latestTermsOfUseVersion?.termsOfUse[0]?.id ?? 1;

    await InsertUserTermsOfUseMutationAsync(
      {
        agreedFromIp: memberIP,
        termsOfUseId: lastTermOfUse,
        livanceCoreId: user.codUsuario,
      },
      {
        onSuccess: () => {
          void redirectToHome();
        },
        onError: () => handleDrawerState("error"),
      },
    );
  };

  const handleDrawerState = (action: string | null, navigateBack = false): void => {
    if (navigateBack) {
      router.history.go(-2);
      return;
    }

    void router.navigate({
      to: "/general-terms/",
      search: { action },
    });
  };

  const showUnexpectedErrorDrawer = (): void => {
    void router.navigate({
      to: "/general-terms/",
      search: { action: "error" },
    });
  };

  const hideUnexpectedErrorDrawer = (): void => {
    if (searchParams.action === "error") {
      router.history.back();
    }
  };

  const handleUnexpectedErrorDrawerToggle = (isOpen: boolean): void => {
    isOpen ? showUnexpectedErrorDrawer() : hideUnexpectedErrorDrawer();
  };

  const redirectToHome = async (): Promise<void> => {
    const pendingTermsOfUseAgreed = "pendingTermsOfUseAgreedNotification";
    const now: Date = zonedDate(new Date());
    await preferencesStorageCache.set(pendingTermsOfUseAgreed, now);

    void router.navigate({
      to: "/calendar/",
    });
  };

  return (
    <>
      <Button
        onClick={() => {
          void onClickHandler();
        }}
        loading={isPending}
        fullWidth
      >
        Li e aceito as condições gerais
      </Button>
      <UnexpectedErrorDrawer
        open={searchParams.action === "error"}
        setOpen={handleUnexpectedErrorDrawerToggle}
      />
    </>
  );
};
